import { COMPACT_HEADER_STYLE } from '../../../lib/gameChrome'
import React from 'react'
import { ActivityIndicator, ScrollView, Text, TouchableOpacity, View } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useQuery } from '@tanstack/react-query'
import { isSelectableBotProfile, STABLE_BOT_CATALOG } from '@warcarp/api-types'
import { apiGet } from '../../../lib/api'
import { goBackOrHome } from '../../../lib/goBack'
import { BotAvatar } from '../../../components/bots/BotAvatar'
import { DiceLuckHistoryPanel } from '../../../components/profile/DiceLuckHistoryPanel'
import { botProfileRecordStats, type BotStatsResponse } from '../../../lib/botStats'

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <View
      className="bg-paper-up border border-olive rounded-lg p-4"
      style={{ width: '48%', marginBottom: 10 }}
    >
      <Text className="font-stencil text-ink-soft text-[10px] uppercase">{label}</Text>
      <Text className="font-stencil text-ink text-lg mt-1">{value}</Text>
    </View>
  )
}

export default function BotProfileScreen() {
  const router = useRouter()
  const { profile: profileParam } = useLocalSearchParams<{ profile: string | string[] }>()
  const profile = Array.isArray(profileParam) ? profileParam[0] : profileParam
  const bot = STABLE_BOT_CATALOG.find((candidate) => candidate.key === profile)
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['bot-leaderboard'],
    queryFn: () => apiGet<BotStatsResponse>('/api/v1/leaderboard/bots?limit=100'),
    enabled: Boolean(bot),
  })
  const stats = data?.entries.find((entry) => entry.botProfile === bot?.key)
  const diceLuck = bot ? data?.diceLuck?.[bot.key] : undefined

  if (!bot) {
    return (
      <SafeAreaView className="flex-1 bg-canvas items-center justify-center px-6">
        <Text className="font-stencil text-ink text-lg uppercase">Bot not found</Text>
        <TouchableOpacity onPress={() => goBackOrHome(router)} className="mt-4">
          <Text className="font-typed text-blood">← Back to Synthetic Command</Text>
        </TouchableOpacity>
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      <View className="flex-row items-center px-4 border-b border-olive" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity
          onPress={() => goBackOrHome(router)}
          accessibilityLabel="Go back"
          accessibilityRole="button"
          className="mr-3 p-1"
        >
          <Text className="font-typed text-blood text-base">← Back</Text>
        </TouchableOpacity>
      </View>

      {isLoading ? (
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator color="#8aa14a" />
        </View>
      ) : error ? (
        <View className="flex-1 items-center justify-center gap-3 px-6">
          <Text className="font-stencil text-ink uppercase">Failed to load bot record</Text>
          <TouchableOpacity
            onPress={() => { void refetch() }}
            className="bg-paper-up border border-olive rounded-md px-4 py-2"
            accessibilityLabel="Retry"
            accessibilityRole="button"
          >
            <Text className="font-stencil text-ink">Retry</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <ScrollView contentContainerStyle={{ padding: 16, gap: 18 }}>
          <View className="flex-row items-center bg-paper border-2 border-olive rounded-xl p-4" style={{ gap: 16 }}>
            <BotAvatar profile={bot.key} size={128} />
            <View
              testID="bot-profile-how"
              className="flex-1 min-w-0 gap-2"
            >
              <Text className="font-stencil text-ink text-2xl uppercase">{bot.displayName}</Text>
              {!isSelectableBotProfile(bot.key) && (
                <View className="self-start rounded border border-olive px-2 py-1" testID="bot-profile-retired">
                  <Text className="font-stencil text-ink-soft text-xs uppercase">Retired</Text>
                </View>
              )}
              <Text className="font-typed text-blood text-sm uppercase">
                {bot.personality.replace('-', ' ')} strategy
              </Text>
              <Text className="font-stencil text-ink-soft text-xs uppercase mt-2">How they play</Text>
              <Text className="font-typed text-ink-soft text-sm leading-5">{bot.description}</Text>
            </View>
          </View>

          <View testID="bot-profile-backstory" className="gap-3">
            {bot.backstory.map((paragraph) => (
              <Text key={paragraph} className="font-typed text-ink-soft text-sm leading-5">
                {paragraph}
              </Text>
            ))}
          </View>

          <View className="gap-2">
            <Text className="font-stencil text-ink text-base uppercase">Record</Text>
            <View className="flex-row flex-wrap justify-between gap-y-2">
              {botProfileRecordStats(stats).map((stat) => (
                <Stat key={stat.label} {...stat} />
              ))}
            </View>
          </View>

          {diceLuck && <DiceLuckHistoryPanel history={diceLuck} />}
        </ScrollView>
      )}
    </SafeAreaView>
  )
}
